/**
 * 修补工具（补齐 registry.ts 早先登记的占位工具）。
 *
 * 两段式手势，跟真实 Photoshop 一致：
 * 1. 在空白处拖拽：自由套索圈一块要修的区域（复用套索的 FreehandGesture/
 *    lassoMask，同一份几何）。松手成一个待定蒙版。
 * 2. 在刚圈出的蒙版内按下拖拽：把这块内容"挪"到新位置预览，松手提交——
 *    app/patch-fill.ts 用拖到的位置的内容（做过色彩匹配）替换原位置。
 * 在蒙版外再拖一次会丢弃旧蒙版、重新圈一块新的（跟 PS 一致）。
 */
import { FreehandGesture, lassoMask } from './marquee';
import { patchFill } from '../../app/patch-fill';
import type { SelectionMask } from '../../engine/selection/mask';
import type { AppStore } from '../../app/store';
import type { ToolManager } from './registry';

export interface PatchPoint {
  readonly x: number;
  readonly y: number;
}

export interface PatchToolDeps {
  readonly store: AppStore;
  readonly tools: ToolManager;
  readonly requestRender: () => void;
}

type PatchState = 'idle' | 'drawing' | 'selected' | 'dragging';

export class PatchToolController {
  readonly #deps: PatchToolDeps;
  readonly #freehand = new FreehandGesture();
  #state: PatchState = 'idle';
  #mask: SelectionMask | null = null;
  #outline: readonly PatchPoint[] = [];
  #dragStart: PatchPoint | null = null;
  #dragCurrent: PatchPoint | null = null;

  constructor(deps: PatchToolDeps) {
    this.#deps = deps;
  }

  get active(): boolean {
    return this.#state !== 'idle';
  }

  handlePointer(phase: 'down' | 'move' | 'up' | 'double', point: PatchPoint): boolean {
    const { store, tools } = this.#deps;
    if (tools.active.id !== 'patch') return false;
    const document = store.document;
    if (document === null) return true;

    if (phase === 'down') {
      if (this.#state === 'selected' && this.#mask !== null && this.#mask.valueAt(Math.floor(point.x), Math.floor(point.y)) > 0) {
        this.#state = 'dragging';
        this.#dragStart = point;
        this.#dragCurrent = point;
      } else {
        // 蒙版外按下：丢弃旧蒙版，重新圈一块
        this.#mask = null;
        this.#outline = [];
        this.#state = 'drawing';
        this.#freehand.begin(point);
      }
      this.#deps.requestRender();
      return true;
    }
    if (phase === 'move') {
      if (this.#state === 'drawing') {
        this.#freehand.add(point);
        this.#deps.requestRender();
      } else if (this.#state === 'dragging') {
        this.#dragCurrent = point;
        this.#deps.requestRender();
      }
      return true;
    }
    if (phase === 'up') {
      if (this.#state === 'drawing') {
        const points = this.#freehand.end();
        if (points !== null) {
          this.#mask = lassoMask(document.size.width, document.size.height, points);
          this.#outline = points;
          this.#state = 'selected';
        } else {
          this.#state = 'idle';
        }
      } else if (this.#state === 'dragging') {
        const mask = this.#mask;
        const start = this.#dragStart;
        const current = this.#dragCurrent ?? point;
        this.#dragStart = null;
        this.#dragCurrent = null;
        this.#state = 'selected';
        if (mask !== null && start !== null) {
          const offsetX = Math.round(current.x - start.x);
          const offsetY = Math.round(current.y - start.y);
          patchFill(store, mask, offsetX, offsetY);
        }
      }
      this.#deps.requestRender();
      return true;
    }
    return true;
  }

  /** 拖拽期间的实时预览：圈选轮廓（黑白虚线，跟套索同一套语言）+ 拖动中额外画一份平移后的轮廓提示落点。 */
  drawOverlay(ctx: CanvasRenderingContext2D): void {
    const strokeOutline = (dx: number, dy: number): void => {
      if (this.#outline.length < 2) return;
      ctx.save();
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(this.#outline[0]!.x + dx, this.#outline[0]!.y + dy);
      for (let i = 1; i < this.#outline.length; i++) {
        ctx.lineTo(this.#outline[i]!.x + dx, this.#outline[i]!.y + dy);
      }
      ctx.closePath();
      for (const color of ['black', 'white'] as const) {
        ctx.strokeStyle = color;
        ctx.lineDashOffset = color === 'black' ? 0 : 4;
        ctx.stroke();
      }
      ctx.restore();
    };

    if (this.#state === 'drawing') {
      const points = this.#freehand.points();
      if (points.length < 2) return;
      ctx.save();
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(points[0]!.x, points[0]!.y);
      for (let i = 1; i < points.length; i++) ctx.lineTo(points[i]!.x, points[i]!.y);
      for (const color of ['black', 'white'] as const) {
        ctx.strokeStyle = color;
        ctx.lineDashOffset = color === 'black' ? 0 : 4;
        ctx.stroke();
      }
      ctx.restore();
      return;
    }
    if (this.#state === 'selected') {
      strokeOutline(0, 0);
      return;
    }
    if (this.#state === 'dragging' && this.#dragStart !== null && this.#dragCurrent !== null) {
      strokeOutline(0, 0);
      strokeOutline(this.#dragCurrent.x - this.#dragStart.x, this.#dragCurrent.y - this.#dragStart.y);
    }
  }
}
